/**
 * Modal Component
 *
 * Base modal wrapper with overlay, centering, and size variants.
 * Handles:
 *   - Escape to close (configurable)
 *   - Body scroll lock while open
 *   - Click-outside to close (configurable)
 *   - Focus trap: focuses the first focusable element on open, traps Tab
 *     inside the modal, and restores focus to the previously-focused
 *     element on close
 *
 * Used as a foundation for FormModal, ConfirmDialog, and other modal types.
 */

import { ReactNode, useEffect, useCallback, useRef } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Whether clicking the overlay closes the modal (default: true) */
  closeOnOverlayClick?: boolean;
  /** Whether pressing Escape closes the modal (default: true) */
  closeOnEscape?: boolean;
  /** Optional aria-labelledby pointing at the modal heading */
  labelledBy?: string;
  /** Extra classes applied to the inner white-box container (use for max-h, flex layouts, etc) */
  contentClassName?: string;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

// Selector for elements that can receive keyboard focus.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({
  isOpen,
  onClose,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  labelledBy,
  contentClassName = '',
}: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Latest onClose / closeOnEscape, read at event time. Parents commonly pass
  // a fresh arrow function every render (useEventDetailModals does, and the
  // event page re-renders on every poll tick), so these must NOT be deps of
  // the focus effect below: re-running it while the modal is open restores
  // focus to the opener and then re-focuses the first focusable element —
  // the X button — mid-typing, and the next Space/Enter closes the modal
  // (rmu feedback 2026-09-18).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeOnEscapeRef = useRef(closeOnEscape);
  closeOnEscapeRef.current = closeOnEscape;

  // Trap Tab / Shift+Tab inside the modal and dispatch Escape.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscapeRef.current) {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !contentRef.current) return;

      const focusables = contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) {
        // Nothing to focus inside the modal — block tabbing out
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    []
  );

  useEffect(() => {
    if (!isOpen) return;

    // Remember which element had focus before we opened so we can restore it.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    // Focus the first focusable element inside the modal. If the modal has
    // a child with autoFocus, the browser will have focused it; otherwise
    // grab the first eligible element.
    const focusFirst = () => {
      if (!contentRef.current) return;
      if (contentRef.current.contains(document.activeElement)) return;
      const first = contentRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (first) first.focus();
      else contentRef.current.focus();
    };
    // Defer one frame so children that mount with autoFocus settle first.
    const raf = requestAnimationFrame(focusFirst);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      // Restore focus to wherever it was before the modal opened.
      previouslyFocused?.focus?.();
    };
    // handleKeyDown is identity-stable (no deps), so this runs only on
    // open/close transitions — never on a parent re-render.
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div
        ref={contentRef}
        className={`bg-white border border-hairline w-full ${sizeClasses[size]} ${contentClassName}`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
